/**
 * The six-box code entry as a pure reducer, so typing, pasting and keyboard
 * moves are testable without a browser. `focus` is the box that should hold
 * the caret after each action.
 */
export const CODE_LENGTH = 6;

export interface OtpState {
  readonly digits: readonly string[];
  readonly focus: number;
}

export type OtpAction =
  /** A change in box `index` — one typed digit, or several from autofill or paste. */
  | { readonly type: 'input'; readonly index: number; readonly raw: string }
  | { readonly type: 'backspace'; readonly index: number }
  | { readonly type: 'move'; readonly index: number; readonly by: -1 | 1 }
  | { readonly type: 'reset' };

export const EMPTY_OTP: OtpState = { digits: Array<string>(CODE_LENGTH).fill(''), focus: 0 };

const clamp = (index: number): number => Math.max(0, Math.min(CODE_LENGTH - 1, index));

function input(state: OtpState, index: number, raw: string): OtpState {
  const found = raw.replace(/\D/g, '');
  const digits = [...state.digits];
  if (found.length <= 1) {
    digits[index] = found;
    return { digits, focus: found === '' ? index : clamp(index + 1) };
  }
  const fits = found.slice(0, CODE_LENGTH - index);
  fits.split('').forEach((digit, k) => {
    digits[index + k] = digit;
  });
  return { digits, focus: clamp(index + fits.length) };
}

function backspace(state: OtpState, index: number): OtpState {
  const digits = [...state.digits];
  if (digits[index] !== '') {
    digits[index] = '';
    return { digits, focus: index };
  }
  if (index === 0) return { digits, focus: 0 };
  digits[index - 1] = '';
  return { digits, focus: index - 1 };
}

export function otpReducer(state: OtpState, action: OtpAction): OtpState {
  switch (action.type) {
    case 'input':
      return input(state, action.index, action.raw);
    case 'backspace':
      return backspace(state, action.index);
    case 'move':
      return { ...state, focus: clamp(action.index + action.by) };
    case 'reset':
      return EMPTY_OTP;
  }
}

export function otpCode(state: OtpState): string {
  return state.digits.join('');
}

export function isComplete(state: OtpState): boolean {
  return /^\d{6}$/.test(otpCode(state));
}
